/* eslint-disable no-nested-ternary */
import { faCircleDot } from "@fortawesome/free-regular-svg-icons/faCircleDot";
import { faCalendar } from "@fortawesome/free-solid-svg-icons/faCalendar";
import { faEnvelope } from "@fortawesome/free-solid-svg-icons/faEnvelope";
import { faFileAlt } from "@fortawesome/free-solid-svg-icons/faFileAlt";
import { faListCheck } from "@fortawesome/free-solid-svg-icons/faListCheck";
import { faPen } from "@fortawesome/free-solid-svg-icons/faPen";
import { faHashtag } from "@fortawesome/free-solid-svg-icons/faHashtag";
import { faPlus } from "@fortawesome/free-solid-svg-icons/faPlus";
import { faTextHeight } from "@fortawesome/free-solid-svg-icons/faTextHeight";
import { faTextWidth } from "@fortawesome/free-solid-svg-icons/faTextWidth";
import { faVoicemail } from "@fortawesome/free-solid-svg-icons/faVoicemail";
import React, { useCallback, useState } from "react";
import { Tooltip } from "../../../../utils/local-antd";
import type { MenuProps } from "../../../../utils/local-antd";

import {
  FieldArrayWithId,
  useFieldArray,
  useFormContext,
} from "react-hook-form";
import { useTranslation } from "react-i18next";
import styled, { css, keyframes } from "styled-components";

import { CreateModuleDefinitionDto } from "../../../../model/module/dto/module-definition/CreateModuleDefinitionDto";
import { UpdateModuleDefinitionDto } from "../../../../model/module/dto/module-definition/UpdateModuleDefinitionDto";
import { Spacing } from "../../../../theme/Spacing";
import { Flex } from "../../../../components/Flex";
import { Rounded } from "../../../../theme/Rounded";
import { Button } from "../../../../components/Button";
import { InputTypes } from "../../../../model/form/types/InputTypes.enum";
import { Lengths } from "../../../../model/shared/enum/Lengths.enum";
import { useStepContext } from "../../../../components/stepper/StepperContext";
import { AtiraIcon } from "../../../../components/AtiraIcon";
import { FontSize, Text } from "../../../../components/Text";
import { useStepperRegisterValidation } from "../../../../components/stepper/useStepperRegisterValidation";
import { Info } from "../../../../components/Info";
import { AtiraDropdown } from "../../../../components/AtiraDropdown";
import { ModuleDefinitionCreateEditUpdateInputModal } from "../components/ModuleDefinitionCreateEditUpdateInputModal";
import { entrySliceSelectors } from "../../../../redux/entry/entry.selector";
import { useAppSelector } from "../../../../redux/store";
import { DNDContext } from "../../../../components/dnd/DNDContext";
import { DNDSortableDroppableContext } from "../../../../components/dnd/DNDSortableDroppableContext";
import {
  DragEndEvent,
  DragOverlay,
  DragStartEvent,
  UniqueIdentifier,
} from "@dnd-kit/core";
import { arrayMove } from "@dnd-kit/sortable";
import clone from "lodash/clone";
import isPlainObject from "lodash/isPlainObject";
import isString from "lodash/isString";
import { faAngleUp } from "@fortawesome/free-solid-svg-icons/faAngleUp";
import { faAngleDown } from "@fortawesome/free-solid-svg-icons/faAngleDown";
import { Breakpoints } from "../../../../theme/Breakpoints";
import { SortableChild } from "../../../../components/dnd/SortableChild";
import useWindowDimensions from "../../../../hooks/useWindowDimensions";
import { ModuleDefinitionTemplates } from "../../../../model/module/types/module-definition/ModuleDefinitionTemplates";
import { faPhone } from "@fortawesome/free-solid-svg-icons/faPhone";
import { ModuleInputCreateEdit } from "../../../../model/module/types/module-definition/ModuleInputCreateEdit";
import isNil from "lodash/isNil";
import { isCustomDateInput } from "../../../../utils/isCustomDateInput";
import { hasBookableSlots } from "../../../../utils/Date";
import { ModuleInput } from "../../../../model/module/types/module-definition/ModuleInput";
import { TextWithTruncated } from "../../../../components/TextWithTruncated";
import { faTrash } from "@fortawesome/free-solid-svg-icons/faTrash";

const nonUserFields = [
  "created",
  "source",
  "form",
  "actions",
  "task",
  "assignee",
  "tags",
];

const FormContainer = styled(Flex)`
  flex-direction: column;
  align-items: center;
  gap: ${Spacing.l};
  overflow: auto;
  min-height: 24rem;
  width: 75%;
  margin: auto;

  @media (min-width: ${Breakpoints.DESKTOP}) {
    width: 50%;
  }
`;

const DroppedInputsWrapper = styled(Flex)`
  width: 100%;
  flex-direction: column;
  gap: ${Spacing.m};
  border-radius: ${Rounded.md};
`;

const FieldWrapper = styled(Flex)<{ valid?: boolean }>`
  width: 100%;
  gap: calc(${Spacing.s} / 2);
  background-color: ${({ theme }) => theme.sub};
  border: ${({ theme, valid }) =>
    `1px solid ${theme[valid ? "darkSub" : "danger"]}`};
  padding: ${Spacing.s} ${Spacing.m};
  border-radius: ${Rounded.md};
  height: 2.5rem;
  cursor: grab;
`;

const colorGlow = keyframes`
  0% {
    color: #f43f42; 
  }
  100% {
    color: #d50000; 
  }
`;

const RequiredStar = styled.span`
  color: ${({ theme }) => theme.danger};
  font-size: 1.2rem;
  width: fit-content;
`;

const Container = styled(Flex)`
  width: 100%;
  gap: ${Spacing.l};
  flex-direction: column;
  align-items: center;
`;

const MoveButtonsWrapper = styled(Flex)`
  gap: ${Spacing.s};
  padding-inline-end: ${Spacing.s};
  border-inline-end: ${({ theme }) => `1px solid ${theme.textColor}`};

  /* The drag handle is enough with a mouse; keep the up/down fallback on touch
     (incl. iPad landscape, which is >= DESKTOP). */
  @media (hover: hover) and (min-width: ${Breakpoints.DESKTOP}) {
    display: none;
  }
`;

const AddFieldButton = styled(Button)`
  display: block;
  margin: auto;
  padding: ${Spacing.s};
  border-radius: ${Rounded.circle};

  &:hover {
    background-color: ${({ theme }) => theme.lightMain};
  }
`;

const StyledList = styled.ul`
  display: flex;
  flex-direction: column;
  gap: ${Spacing.s};
  list-style-position: inside;
`;

const IconButton = styled(Button)<{ warning?: boolean }>`
  background-color: transparent;
  color: ${({ theme }) => theme.main};
  padding: ${Spacing.xs};
  margin: 0;
  font-size: ${FontSize.m};

  ${({ warning, theme }) =>
    warning &&
    css`
      svg {
        color: ${theme.danger};
        animation: ${colorGlow} 0.5s infinite alternate ease-in-out;
      }
    `}
`;

const inputOptions: ModuleInputCreateEdit[] = [
  {
    type: InputTypes.TEXT,
    settings: { required: false, maxChar: Lengths.DESCRIPTION },
    icon: faTextHeight,
    name: "",
  },
  {
    type: InputTypes.NUMBER,
    settings: { required: false, maxChar: Lengths.NUMBER },
    icon: faHashtag,
    name: "",
  },
  {
    type: InputTypes.PHONE,
    settings: { required: false, maxChar: Lengths.PHONE },
    icon: faPhone,
    name: "",
  },
  {
    settings: { required: false, maxChar: Lengths.DESCRIPTION },
    type: InputTypes.TEXTAREA,
    icon: faTextWidth,
    name: "",
  },
  {
    settings: { required: false },
    type: InputTypes.DATE,
    icon: faCalendar,
    name: "",
  },
  {
    settings: { required: false, maxChar: Lengths.EMAIL },
    type: InputTypes.EMAIL,
    icon: faVoicemail,
    name: "",
  },
  {
    settings: { required: false },
    type: InputTypes.SELECT,
    icon: faVoicemail,
    name: "",
  },
  {
    settings: { required: false },
    type: InputTypes.RADIO,
    icon: faCircleDot,
    name: "",
  },
];

const getDefaultMaxChar = (type: InputTypes) => {
  switch (type) {
    case InputTypes.NUMBER.toLowerCase():
      return Lengths.NUMBER;
    case InputTypes.EMAIL.toLowerCase():
      return Lengths.EMAIL;
    default:
      return Lengths.DESCRIPTION;
  }
};

const getMinOptionsLength = (type: InputTypes) => {
  if (type.toLowerCase() === InputTypes.RADIO.toLowerCase()) {
    return 2;
  }

  return 1;
};

export const ModuleDefinitionCreateEditFieldsStep: React.FC = () => {
  const [activeItemId, setActiveItemId] = useState<
    UniqueIdentifier | undefined
  >();

  const { control, getValues, watch, formState, setError, clearErrors } =
    useFormContext<
      (CreateModuleDefinitionDto | UpdateModuleDefinitionDto) & {
        template: ModuleDefinitionTemplates;
      }
    >();

  const { append, replace, fields, remove } = useFieldArray({
    control,
    name: "inputs",
  });

  const [inputUpdateModalVisible, setInputUpdateModalVisible] = useState(false);
  const [selectedInputIndex, setSelectedInputIndex] = useState(0);
  const [inputsDropdownOpen, setInputsDropdownOpen] = useState(false);

  const { width } = useWindowDimensions();
  const { currentStep } = useStepContext();
  const { t } = useTranslation();

  const inputs = watch("inputs");

  const tableColumns = useAppSelector(
    entrySliceSelectors.selectUserEntryTableSettings,
  )?.columns;

  const userFieldsCount =
    tableColumns?.filter((c) => !nonUserFields.includes(c.field))?.length || 0;

  const dragIconStyle = {
    insetInlineStart: "-2rem",
    transform: "translateY(175%)",
    display: width > 992 ? "block" : "none",
  };

  const usedFields = getValues("inputs");

  const canAddField = (inputs?.length || 0) < userFieldsCount;

  const getInputIcon = (type: InputTypes) => {
    switch (type?.toLowerCase()) {
      case InputTypes.TEXTAREA:
        return faTextHeight;
      case InputTypes.DATE:
        return faCalendar;
      case InputTypes.EMAIL:
        return faEnvelope;
      case InputTypes.NUMBER:
        return faHashtag;
      case InputTypes.SELECT:
        return faListCheck;
      case InputTypes.RADIO:
        return faCircleDot;
      case InputTypes.PHONE:
        return faPhone;
      default:
        return faTextWidth;
    }
  };

  const fieldOptions = [
    {
      key: "default",
      type: "group",
      children: inputOptions.map((x, i) => ({
        label: (
          <Flex alignItems="center" gap="m">
            <AtiraIcon icon={getInputIcon(x.type)} color="main" size="lg" />
            <Text color="main" fontSize="s">
              {t(`common.${x.type.toLowerCase()}`)}
            </Text>
          </Flex>
        ),
        key: x.type,
        onClick: () => onAddDefaultInput(x),
      })),
    },
  ] as MenuProps["items"];

  const canGoNext = useCallback(() => {
    const getMinOptionsLength = (type: InputTypes) => {
      if (type.toLowerCase() === InputTypes.RADIO.toLowerCase()) {
        return 2;
      }

      return 1;
    };

    const optionsInputs = inputs?.filter(
      (inp) =>
        inp?.type?.toLowerCase() === InputTypes.SELECT.toLowerCase() ||
        inp?.type?.toLowerCase() === InputTypes.RADIO.toLowerCase(),
    );
    const customDateInputs = inputs?.filter((inp) =>
      isCustomDateInput(inp?.type, inp?.dateInputConfig),
    );

    const dateInputsValid = customDateInputs?.length
      ? !customDateInputs.some((inp) => {
          const missingSlots = !hasBookableSlots(inp?.dateInputConfig);
          const missingTz = !inp?.dateInputConfig?.timezone?.length;
          return missingSlots || missingTz;
        })
      : true;

    const optionsInputsValid = optionsInputs?.length
      ? optionsInputs.every(
          (inp) => (inp?.options?.length || 0) >= getMinOptionsLength(inp.type),
        )
      : true;

    const labelsValid = (inputs || [])?.every(
      (inp) => (inp?.label?.length || 0) >= 1,
    );

    return dateInputsValid && optionsInputsValid && labelsValid;
  }, [inputs]);

  useStepperRegisterValidation(currentStep, canGoNext);

  const onDragStart = (event: DragStartEvent) => {
    const { active } = event;
    setActiveItemId(active.id);
  };

  const onDragEnd = ({ active, over }: DragEndEvent) => {
    if (!over || active.id === over.id) {
      return;
    }

    const activeIndex = fields.findIndex((item) => item.id === active.id);
    const overIndex = fields.findIndex((item) => item.id === over.id);

    moveErrorMessage(activeIndex, overIndex);

    const newInputs = arrayMove(clone(inputs || []), activeIndex, overIndex);

    replace(newInputs);
    setActiveItemId(undefined);
  };

  const onMoveUp = (movedItemId: string) => {
    const activeIndex = fields.findIndex((item) => item.id === movedItemId);
    const overIndex = activeIndex - 1;
    if (overIndex > -1) {
      moveErrorMessage(activeIndex, overIndex);

      const newInputs = arrayMove(clone(inputs || []), activeIndex, overIndex);

      replace(newInputs);
    }
  };

  const onMoveDown = (movedItemId: string) => {
    const activeIndex = fields.findIndex((item) => item.id === movedItemId);
    const overIndex = activeIndex + 1;
    if (overIndex < fields.length) {
      moveErrorMessage(activeIndex, overIndex);

      const newInputs = arrayMove(clone(inputs || []), activeIndex, overIndex);

      replace(newInputs);
    }
  };

  const getFieldError = (
    index: number,
  ): { path: string; message: string } | null => {
    const inputError = formState.errors?.inputs?.[index];

    if (!inputError) {
      return null;
    }

    let result: { path: string; message: string } | null = null;

    const traverse = (node: any, path: string) => {
      if (result) {
        return;
      }

      if (Array.isArray(node)) {
        node.forEach((item, i) => traverse(item, `${path}.${i}`));
      } else if (isPlainObject(node)) {
        for (const key in node) {
          const value = node[key];

          if (key === "message" && isString(value)) {
            result = {
              path,
              message: value,
            };
            return;
          }

          traverse(value, `${path}.${key}`);
          if (result) {
            return;
          }
        }
      }
    };

    traverse(inputError, `inputs.${index}`);
    return result;
  };

  const moveErrorMessage = (activeIndex: number, overIndex: number) => {
    const activeItemError = getFieldError(activeIndex);
    const overItemError = getFieldError(overIndex);

    if (activeItemError?.message && activeItemError?.path) {
      setError(
        activeItemError.path.replace(
          /inputs\.(\d+)/,
          `inputs.${overIndex}`,
        ) as any,
        {
          message: activeItemError?.message,
        },
      );

      clearErrors(`inputs.${activeIndex}`);
    } else if (overItemError?.message && overItemError?.path) {
      setError(
        overItemError.path.replace(
          /inputs\.(\d+)/,
          `inputs.${activeIndex}`,
        ) as any,
        {
          message: overItemError?.message,
        },
      );

      clearErrors(`inputs.${overIndex}`);
    }
  };

  const onAddDefaultInput = (x: ModuleInputCreateEdit) => {
    const hasMaxChar = ![
      InputTypes.DATE,
      InputTypes.SELECT,
      InputTypes.RADIO,
    ].includes(x.type.toLowerCase() as InputTypes);

    append({
      ...x,
      label: t(`common.${x.type.toLowerCase()}`),
      order: fields.length,
      name: `${x.type}-${Date.now()}`,
      settings: {
        required: false,
        ...(hasMaxChar
          ? { maxChar: getDefaultMaxChar(x.type.toLowerCase() as InputTypes) }
          : {}),
      },
    });
  };

  const onEditInput = (inputIndex: number) => {
    setSelectedInputIndex(inputIndex);
    setInputUpdateModalVisible(true);
  };

  const getFieldRender = (
    input?: FieldArrayWithId<CreateModuleDefinitionDto, "inputs", "id">,
    index?: number,
    invalid?: boolean,
    isDragOverlay?: boolean,
  ) => {
    if (isDragOverlay && !activeItemId) {
      return null;
    }

    if (!isDragOverlay && isNil(index)) {
      return null;
    }

    const inputIndex = isDragOverlay
      ? fields.findIndex((item) => item.id === activeItemId)
      : index;

    const liveInput = getValues(`inputs.${inputIndex}` as any) as ModuleInput;

    if (!liveInput || isNil(inputIndex)) {
      return null;
    }

    const inputOptions = liveInput.options;

    const isRadio =
      liveInput?.type?.toLowerCase() === InputTypes.RADIO.toLowerCase();
    const isSelect =
      liveInput?.type?.toLowerCase() === InputTypes.SELECT.toLowerCase();

    const displaySelectOptionsError = isSelect && !inputOptions?.length;
    const displayRadioOptionsError =
      isRadio && (!inputOptions?.length || inputOptions?.length < 2);

    // Matches canGoNext's customDateInputs gate - otherwise an hours- or
    // timezone-only config can block Next with no visible reason.
    const isBookingConfig = isCustomDateInput(
      liveInput?.type,
      liveInput?.dateInputConfig,
    );
    const missingSlots = !hasBookableSlots(liveInput?.dateInputConfig);
    const missingTz = !liveInput?.dateInputConfig?.timezone?.length;
    const displayCustomDateError =
      isBookingConfig && (missingSlots || missingTz);

    return (
      <Flex flexDirection="column" gap="xs">
        <FieldWrapper
          valid={
            !invalid && !displaySelectOptionsError && !displayRadioOptionsError
          }
        >
          <Flex alignItems="center" gap="m">
            <AtiraIcon
              icon={getInputIcon(input?.type?.toLowerCase() as InputTypes)}
              color="main"
              size="lg"
              width={"1rem"}
            />

            <Flex alignItems="center" gap="xs">
              <TextWithTruncated
                length={Lengths.NAME}
                style={{ userSelect: "none" }}
              >
                {liveInput?.label}
              </TextWithTruncated>
              {liveInput?.settings?.required ? (
                <RequiredStar>*</RequiredStar>
              ) : null}
            </Flex>
          </Flex>

          <Flex alignItems="center" gap="s" marginInlineStart="auto">
            <MoveButtonsWrapper>
              <IconButton
                icon={faAngleUp}
                onClick={() => onMoveUp(input?.id || "")}
                disabled={index === 0 || isDragOverlay}
              />

              <IconButton
                icon={faAngleDown}
                onClick={() => onMoveDown(input?.id || "")}
                disabled={index === fields.length - 1 || isDragOverlay}
              />
            </MoveButtonsWrapper>

            <Flex gap="s">
              <Tooltip title={t("common.edit")}>
                <IconButton
                  onClick={() => onEditInput(inputIndex)}
                  icon={faPen}
                  warning={(inputs?.[inputIndex]?.label?.length || 0) < 1}
                />
              </Tooltip>

              <IconButton
                onClick={() => remove(index)}
                disabled={
                  fields?.length <= 1 ||
                  // @ts-ignore
                  input?.isDeletable === false ||
                  isDragOverlay
                }
                icon={faTrash}
              />
            </Flex>
          </Flex>
        </FieldWrapper>

        {displaySelectOptionsError ? (
          <Text color="danger" fontSize="s">
            * {t("forms.create.step.2.items.empty")}
          </Text>
        ) : displayRadioOptionsError ? (
          <Text color="danger" fontSize="s">
            * {t("forms.create.step.2.items.lessThanTwo")}
          </Text>
        ) : null}

        {invalid ? (
          <Text color="danger" fontSize="s">
            <span>* </span>
            {getFieldError(inputIndex)?.message ||
              t("forms.create.step.2.label.short")}
          </Text>
        ) : null}

        {displayCustomDateError ? (
          <Text color="danger" fontSize="s">
            *
            {t(
              "forms.create.step.2.customDateInput.errors.incompleteConfiguration",
            )}
          </Text>
        ) : null}
      </Flex>
    );
  };

  return (
    <Container>
      <Flex flexDirection="column" gap="s" alignItems="center" marginBottom="l">
        <Flex alignItems="center" gap="s" marginBottom="m">
          <AtiraIcon icon={faFileAlt} color="main" size="2x" />

          <Text color="main" fontSize="l" fontWeight={"bold"}>
            {t("modules.definition.fields.title")}
          </Text>
        </Flex>

        <Info
          info={
            <StyledList>
              <li>
                {t("modules.definition.fields.description-1", {
                  fieldsLimit: userFieldsCount,
                })}
              </li>

              <li>{t("modules.definition.fields.description-2")}</li>
            </StyledList>
          }
        />
      </Flex>

      <FormContainer>
        <DNDContext onDragStart={onDragStart} onDragEnd={onDragEnd}>
          <DroppedInputsWrapper>
            <DNDSortableDroppableContext id="dropZone" items={fields}>
              <Flex width={"100%"} flexDirection="column" gap="m">
                {fields.map((input, index) => {
                  const invalid = Boolean(
                    formState.errors.inputs?.[index] ||
                      (usedFields?.[index]?.label?.length || 0) < 1,
                  );

                  return (
                    <SortableChild
                      key={input.id}
                      id={input.id}
                      dragIconStyle={dragIconStyle}
                      iconSize="xl"
                    >
                      {getFieldRender(input, index, invalid)}
                    </SortableChild>
                  );
                })}

                <AtiraDropdown
                  items={fieldOptions}
                  trigger={["click"]}
                  open={inputsDropdownOpen}
                  onOpenChange={setInputsDropdownOpen}
                  styles={{ root: { width: "15rem" } }}
                  placement="bottomCenter"
                >
                  <Tooltip
                    title={
                      !canAddField
                        ? t("modules.definition.fields.reachedLimit")
                        : undefined
                    }
                  >
                    <AddFieldButton icon={faPlus} disabled={!canAddField} />
                  </Tooltip>
                </AtiraDropdown>
              </Flex>
            </DNDSortableDroppableContext>

            <DragOverlay
              dropAnimation={{
                duration: 500,
                easing: "cubic-bezier(0.18, 0.67, 0.6, 1.22)",
              }}
            >
              {getFieldRender(undefined, undefined, undefined, true)}
            </DragOverlay>
          </DroppedInputsWrapper>
        </DNDContext>
      </FormContainer>

      <ModuleDefinitionCreateEditUpdateInputModal
        key={selectedInputIndex}
        inputIndex={selectedInputIndex}
        isOpen={inputUpdateModalVisible}
        onClose={() => setInputUpdateModalVisible(false)}
      />
    </Container>
  );
};
